// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { db } from '@/lib/db'
import { WishCategory } from '@/types'
import { generateUUID, nowISO, addYearsISO } from '@/lib/utils'
import type { UserProfile, DecryptedProfile, Result } from '@/types'
import { SyncStatus, CompletionStatus } from '@/types'

// In-memory masterKey storage (never persisted)
const masterKeyStore = new Map<string, CryptoKey>()

// ── Key derivation ─────────────────────────────────────────────────────────────
async function deriveMasterKey(password: string, userId: string): Promise<CryptoKey> {
  const encoder  = new TextEncoder()
  const keyMat   = await crypto.subtle.importKey(
    'raw', encoder.encode(password), 'PBKDF2', false, ['deriveBits', 'deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name:       'PBKDF2',
      hash:       'SHA-256',
      salt:       encoder.encode(userId),
      iterations: 600_000,
    },
    keyMat,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

// ── Encryption helpers ────────────────────────────────────────────────────────
async function encryptBlob(data: object, key: CryptoKey): Promise<{ blob: ArrayBuffer; iv: string }> {
  const iv      = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(JSON.stringify(data))
  const blob    = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)
  return { blob, iv: btoa(String.fromCharCode(...iv)) }
}

async function decryptBlob<T>(blob: ArrayBuffer, ivB64: string, key: CryptoKey): Promise<T> {
  const iv        = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0))
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, blob)
  return JSON.parse(new TextDecoder().decode(decrypted)) as T
}

// ── Public API ─────────────────────────────────────────────────────────────────

export async function createProfile(
  firstName:   string,
  profile:     DecryptedProfile,
  password:    string,
): Promise<Result<{ userId: string }>> {
  try {
    const userId    = generateUUID()
    const masterKey = await deriveMasterKey(password, userId)
    const { blob, iv } = await encryptBlob(profile, masterKey)

    const userProfile: UserProfile = {
      id:               userId,
      createdAt:        nowISO(),
      updatedAt:        nowISO(),
      firstName,
      encryptedProfile: blob,
      blobIv:           iv,
      blobVersion:      1,
      cloudSyncEnabled: false,
      cloudProfileId:   null,
      lastLocalSave:    nowISO(),
      schemaVersion:    1,
      syncStatus:       SyncStatus.LOCAL_ONLY,
    }

    await db.user_profiles.add(userProfile)

    // Store masterKey in memory
    masterKeyStore.set(userId, masterKey)

    // Create inactivity protocol (disabled by default)
    await db.inactivity_protocols.add({
      id:                  generateUUID(),
      userId,
      enabled:             false,
      thresholdDays:       90,
      lastOwnerLoginAt:    nowISO(),
      activationTriggered: false,
      triggeredAt:         null,
      warningsSent:        0,
      lastWarningSentAt:   null,
    })

    return { succes: true, data: { userId }, error: null }
  } catch (err) {
    console.error('createProfile error:', err)
    return {
      succes: false,
      data:   null,
      error: {
        code:     'PROFILE_CREATE_FAILED',
        message:  'Het aanmaken van uw profiel is mislukt. Probeer opnieuw.',
        severity: 'HIGH',
      },
    }
  }
}

export async function loadProfile(
  userId:   string,
  password: string,
): Promise<Result<{ profile: UserProfile; decrypted: DecryptedProfile }>> {
  try {
    const profile = await db.user_profiles.get(userId)
    if (!profile) {
      return {
        succes: false,
        data:   null,
        error: {
          code:     'PROFILE_NOT_FOUND',
          message:  'Profiel niet gevonden.',
          severity: 'HIGH',
        },
      }
    }

    const masterKey = await deriveMasterKey(password, userId)
    const decrypted = await decryptBlob<DecryptedProfile>(
      profile.encryptedProfile, profile.blobIv, masterKey,
    )

    masterKeyStore.set(userId, masterKey)

    // Update last login
    await db.inactivity_protocols
      .where('userId').equals(userId)
      .modify({ lastOwnerLoginAt: nowISO() })

    return { succes: true, data: { profile, decrypted }, error: null }
  } catch {
    return {
      succes: false,
      data:   null,
      error: {
        code:     'AUTH_WRONG_PASSWORD',
        message:  'Onjuist wachtwoord. Probeer opnieuw.',
        severity: 'MEDIUM',
      },
    }
  }
}

export function getMasterKey(userId: string): CryptoKey | null {
  return masterKeyStore.get(userId) ?? null
}

/**
 * Haal adresvelden op uit het versleuteld profiel (in-memory masterKey vereist).
 * Gebruikt als fallback wanneer wizard_personal nog niet bestaat.
 * Returns null als masterKey niet beschikbaar is (gebruiker niet ingelogd).
 */
export async function getProfileAddress(userId: string): Promise<{
  street: string; houseNumber: string; houseNumberAddition: string
  postalCode: string; city: string
} | null> {
  try {
    const key = getMasterKey(userId)
    if (!key) return null
    const profile = await getFirstProfile()
    if (!profile || !profile.encryptedProfile || !profile.blobIv) return null

    const decrypted = await decryptBlob<{
      street?: string; houseNumber?: string; houseNumberAddition?: string
      address?: string
    }>(profile.encryptedProfile, profile.blobIv, key)

    let street              = decrypted.street              ?? ''
    let houseNumber         = decrypted.houseNumber         ?? ''
    let houseNumberAddition = decrypted.houseNumberAddition ?? ''

    // Extraheer postcode + stad uit legacy address-string
    const raw   = (decrypted.address ?? '').trim()
    const parts = raw.split(/\s+/)
    const singleToken = parts.length >= 1 && /^\d{4}[A-Z]{2}$/.test(parts[0])
    const twoToken    = parts.length >= 2 && /^\d{4}$/.test(parts[0]) && /^[A-Z]{2}$/.test(parts[1])
    let postalCode = ''
    let city       = ''
    if (singleToken) {
      postalCode = parts[0]
      city       = parts.slice(1).join(' ')
    } else if (twoToken) {
      postalCode = `${parts[0]} ${parts[1]}`
      city       = parts.slice(2).join(' ')
    } else if (street) {
      const pcMatch = raw.match(/\b(\d{4}\s?[A-Z]{2})\s+(.+)$/)
      if (pcMatch) { postalCode = pcMatch[1]; city = pcMatch[2] }
    }

    // V40C-P1: Fallback naar wizard_personal als onboarding-blob leeg is
    // IntakePage/Step01 slaan adres op in wizard_personal, niet in encryptedProfile
    if (!street && !postalCode) {
      try {
        const personalRec = await db.wishes
          .where('[userId+category]').equals([userId, WishCategory.PERSONAL])
          .toArray()
        // Records saved by Step01PersonalData via add() have no moduleKey field
        // So we match either: explicit moduleKey='wizard_personal' OR first record in category
        const wizardPersonal = personalRec.find(
          (r) => (r as unknown as { moduleKey?: string }).moduleKey === 'wizard_personal'
        ) ?? personalRec[0]  // fallback: eerste record in PERSONAL categorie
        if (wizardPersonal?.encryptedContent && wizardPersonal.blobIv) {
          const pd = await decryptBlob<{
            street?: string; houseNumber?: string; houseNumberAddition?: string
            postalCode?: string; city?: string
          }>(wizardPersonal.encryptedContent, wizardPersonal.blobIv, key)
          if (pd) {
            street              = pd.street              ?? ''
            houseNumber         = pd.houseNumber         ?? ''
            houseNumberAddition = pd.houseNumberAddition ?? ''
            postalCode          = pd.postalCode          ?? ''
            city                = pd.city                ?? ''
          }
        }
      } catch { /* fallback mislukt - gebruik bestaande waarden */ }
    }

    return { street, houseNumber, houseNumberAddition, postalCode, city }
  } catch { return null }
}


/**
 * Laad de geboortedatum van de profieleigenaar voor dobHash-berekening.
 * Volgorde:
 *   1. wizard_personal.dateOfBirth  (meest actueel - wizard stap 0)
 *   2. encryptedProfile.dateOfBirth (onboarding-waarde als fallback)
 * Returns '' als beide leeg zijn (masterKey niet beschikbaar of DOB nooit ingevuld).
 * NOOIT een hardcoded fallback gebruiken - lege waarde blokkeert VP-aanmaak.
 */
export async function loadOwnerDob(userId: string): Promise<string> {
  try {
    // Stap 1: wizard_personal (meest actueel)
    const key = getMasterKey(userId)
    if (key) {
      // Gebruik direct de reeds geïmporteerde db instantie
      const rec = await db.wishes
        .where('[userId+category]')
        .equals([userId, 'OTHER'])
        .filter((r) => (r as unknown as { moduleKey?: string }).moduleKey === 'wizard_personal')
        .first()
      if (rec?.encryptedContent) {
        const iv  = Uint8Array.from(atob(rec.blobIv ?? ''), c => c.charCodeAt(0))
        const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, rec.encryptedContent)
        const personal = JSON.parse(new TextDecoder().decode(dec)) as { dateOfBirth?: string }
        if (personal?.dateOfBirth) return personal.dateOfBirth
      }

      // Stap 2: encryptedProfile (onboarding-waarde)
      const profile = await getFirstProfile()
      if (profile?.encryptedProfile && profile.blobIv) {
        const decrypted = await decryptBlob<{ dateOfBirth?: string }>(
          profile.encryptedProfile, profile.blobIv, key,
        )
        if (decrypted?.dateOfBirth) return decrypted.dateOfBirth
      }
    }
  } catch { /* masterKey niet beschikbaar of decryptie mislukt */ }
  return ''
}


export async function getProfile(userId: string): Promise<UserProfile | undefined> {
  return db.user_profiles.get(userId)
}

export async function profileExists(): Promise<boolean> {
  const count = await db.user_profiles.count()
  return count > 0
}

export async function getFirstProfile(): Promise<UserProfile | undefined> {
  return db.user_profiles.toCollection().first()
}

export async function updateProfile(
  userId:    string,
  firstName: string,
  profile:   DecryptedProfile,
): Promise<Result<void>> {
  try {
    const masterKey = masterKeyStore.get(userId)
    if (!masterKey) {
      return {
        succes: false,
        data:   null,
        error: { code: 'AUTH_REQUIRED', message: 'Opnieuw inloggen vereist.', severity: 'HIGH' },
      }
    }
    const { blob, iv } = await encryptBlob(profile, masterKey)
    await db.user_profiles.update(userId, {
      firstName,
      encryptedProfile: blob,
      blobIv:           iv,
      updatedAt:        nowISO(),
      lastLocalSave:    nowISO(),
    })
    return { succes: true, data: undefined, error: null }
  } catch (err) {
    console.error('updateProfile error:', err)
    return {
      succes: false,
      data:   null,
      error: { code: 'PROFILE_UPDATE_FAILED', message: 'Bijwerken mislukt.', severity: 'MEDIUM' },
    }
  }
}

export async function deleteProfile(userId: string): Promise<Result<void>> {
  try {
    await db.user_profiles.delete(userId)
    await db.trusted_persons.where('userId').equals(userId).delete()
    await db.access_keys.where('userId').equals(userId).delete()
    await db.key_permissions.where('userId').equals(userId).delete()
    await db.messages.where('userId').equals(userId).delete()
    await db.wishes.where('userId').equals(userId).delete()
    await db.funeral_wishes.where('userId').equals(userId).delete()
    await db.medical_wishes.where('userId').equals(userId).delete()
    await db.document_index.where('userId').equals(userId).delete()
    await db.digital_estate.where('userId').equals(userId).delete()
    await db.personal_farewell.where('userId').equals(userId).delete()
    await db.inactivity_protocols.where('userId').equals(userId).delete()
    await db.module_progress.where('userId').equals(userId).delete()
    // Anonymise logs
    await db.access_log.where('userId').equals(userId).modify({
      userId:   'DELETED',
      ipRegion: null,
    })
    masterKeyStore.delete(userId)
    return { succes: true, data: undefined, error: null }
  } catch (err) {
    console.error('deleteProfile error:', err)
    return {
      succes: false,
      data:   null,
      error: { code: 'PROFILE_DELETE_FAILED', message: 'Verwijderen mislukt.', severity: 'HIGH' },
    }
  }
}

export async function saveModuleProgress(
  userId:     string,
  moduleName: string,
  current:    number,
  total:      number,
): Promise<void> {
  const existing = await db.module_progress
    .where('[userId+moduleName]').equals([userId, moduleName])
    .first()

  const percentage = total > 0 ? Math.round((current / total) * 100) : 0
  const status: CompletionStatus =
    current === 0     ? CompletionStatus.NOT_STARTED :
    current >= total  ? CompletionStatus.COMPLETE    : CompletionStatus.IN_PROGRESS

  if (existing) {
    await db.module_progress.update(existing.id, {
      stepsCurrent:         current,
      stepsTotal:           total,
      completionPercentage: percentage,
      status,
      lastVisitedAt:        nowISO(),
      completedAt:          status === 'COMPLETE' ? nowISO() : existing.completedAt,
    })
  } else {
    await db.module_progress.add({
      id:                   generateUUID(),
      userId,
      moduleName,
      completionPercentage: percentage,
      stepsCurrent:         current,
      stepsTotal:           total,
      status,
      startedAt:            current > 0 ? nowISO() : null,
      completedAt:          status === 'COMPLETE' ? nowISO() : null,
      lastVisitedAt:        nowISO(),
      syncStatus:           SyncStatus.PENDING,
      cloudSyncId:          null,
    })
  }
}

export async function getAllModuleProgress(userId: string) {
  return db.module_progress.where('userId').equals(userId).toArray()
}

export async function getRetentionDate(): Promise<string> {
  return addYearsISO(7)
}
